// Cards used by more than one screen: the CLI / API 2-split cards (home 'me', person detail), team comparison rows
// (home 'all', people teams tab) and member usage rows (home 'team'). Costs are per kind, never summed.
import { z } from 'zod';
import { ConnectionIssueKey, ConnectionStatus, DedupState, MissingReason, Tool } from '../enums.js';
import { CardFooter, Comparison, Count, EpochMs, Pct, PersonRef, RoleRef, TeamRef, ValuedNumber, Ymd } from '../common.js';
import { SplitRatio } from '../charts.js';

const AuthModeCounts = z.object({ subscription: Count, apiKey: Count, unknown: Count });
const CliToolRow = z.object({ tool: z.enum(['claude_code', 'codex']), sessions: Count, prompts: Count, tokens: ValuedNumber, activeMs: ValuedNumber, share: Pct.nullable() });

/** "CLI 도구" card (plan 8.4.2): prompts · usage · active time (Claude only) · tool-estimated cost. */
export const CliSplitCard = z.object({
  sessions: Count,
  prompts: Comparison,
  /** partial · verifying while Codex totals are being verified. */
  tokens: Comparison,
  /** Claude Code only; Codex rows carry none · tool_not_sent · codex_active. */
  activeMs: Comparison,
  /** Claude estimate with its change chip; Codex unofficial is a separate value, never added. */
  cost: z.object({ cliEstimate: Comparison, unofficial: ValuedNumber }),
  /**
   * "결제 방식별 사용량" (plan 5.3, FR-34): `tokens` is the shown unit and sums to the card's token total (QA-10).
   * `sessions` = conversations by the same split (the mockup's "대화 수 기준" line). Claude values are the device's last
   * check, hence `estimated`.
   */
  authModes: z.object({ tokens: AuthModeCounts, sessions: AuthModeCounts, estimated: z.boolean() }),
  cacheReadShare: Pct.nullable(),
  tools: z.array(CliToolRow),
  /** "CLI(게이트웨이 경유) n건 · 중복 확인 중" — shown apart, added to no card (plan 5.4). */
  viaGateway: z.object({ calls: Count, dedupState: DedupState }),
  footer: CardFooter,
});
export type CliSplitCard = z.infer<typeof CliSplitCard>;

/**
 * The CLI card on someone's person page (SCR-07): no AI active time — never shown per person (plan 7.6, 8.4.7;
 * mockup "people.d.noTime").
 */
export const PersonCliCard = CliSplitCard.omit({ activeMs: true }).extend({
  sessions: Count.nullable(),
  authModes: CliSplitCard.shape.authModes.nullable(),
  viaGateway: CliSplitCard.shape.viaGateway.extend({ calls: Count.nullable() }),
  tools: z.array(CliToolRow.omit({ activeMs: true })),
});
export type PersonCliCard = z.infer<typeof PersonCliCard>;

/** "API 키 호출" card: calls · usage · wait (typical/slow) · price-table cost; no active time. */
export const ApiSplitCard = z.object({
  /** false before P4 / gateway off → "기록 없음 · 게이트웨이를 거친 호출만 사람별로 보여요". */
  gatewayEnabled: z.boolean(),
  sessions: Count,
  calls: Comparison,
  tokens: Comparison,
  latency: z.object({ p50Ms: ValuedNumber, p95Ms: ValuedNumber, n: Count }),
  cost: z.object({ gatewayPrice: Comparison }),
  tools: z.array(z.object({ tool: z.enum(['anthropic_api', 'openai_api']), calls: Count, tokens: ValuedNumber, share: Pct.nullable() })),
  /** Org report of the viewer's keys: "조직 보고서(하루 단위·사람별 아님)" — never added to person totals. */
  orgReport: z.object({ rows: Count, keys: Count, days: Count, tokens: ValuedNumber, costMicros: ValuedNumber }).nullable(),
  footer: CardFooter,
});
export type ApiSplitCard = z.infer<typeof ApiSplitCard>;
/** Null counts on an individual view mean permission/dirty/absent data, never a fabricated zero. */
export const PersonApiCard = ApiSplitCard.extend({ sessions: Count.nullable(), latency: ApiSplitCard.shape.latency.extend({ n: Count.nullable() }) });

/** `orgBill` null = no keys of this team are in the org report ("키 없음"). */
export const CostColumns = z.object({ cliEstimate: ValuedNumber, unofficial: ValuedNumber, gatewayPrice: ValuedNumber, orgBill: ValuedNumber.nullable() });

/** Per-tool split behind a stacked bar; the card's metric switch (tokens · prompts · conversations) picks one. */
export const ToolSplit = z.partialRecord(Tool, z.object({ tokens: Count, prompts: Count, sessions: Count }));
export type ToolSplit = z.infer<typeof ToolSplit>;

/** One team in a comparison (name order, not a ranking). Team totals use the team at the time of each record. */
export const TeamCompareRow = z.object({
  team: TeamRef,
  members: Count,
  active: Count,
  prompts: Count,
  sessions: Count,
  tokens: ValuedNumber,
  byTool: ToolSplit,
  ratio: z.object({ tokens: SplitRatio, counts: SplitRatio }),
  costs: CostColumns,
  /** Link to the people tab filtered to this team. */
  canDrill: z.boolean(),
});
export type TeamCompareRow = z.infer<typeof TeamCompareRow>;

/**
 * "소규모 팀 합산" row (plan 7.6): teams under 3 people merged. `hidden` (only one small team, or still < 3 people
 * together): every value none · small_group and `teamCount`/`people`/`active`/`byTool`/`ratio` null.
 */
export const SmallTeamsRow = z.object({
  teamCount: Count.nullable(),
  hidden: z.boolean(),
  people: Count.nullable(),
  active: Count.nullable(),
  prompts: ValuedNumber,
  sessions: ValuedNumber,
  tokens: ValuedNumber,
  byTool: ToolSplit.nullable(),
  ratio: z.object({ tokens: SplitRatio, counts: SplitRatio }).nullable(),
  costs: CostColumns,
});
export type SmallTeamsRow = z.infer<typeof SmallTeamsRow>;

/** Team member usage (home 'team', name order). Former members appear with their pre-move records (record-time team). */
export const MemberUsageRow = z.object({
  person: PersonRef,
  current: z.boolean(),
  /** Former member: moved on `date` to `team`. */
  moved: z.object({ date: Ymd, team: TeamRef.nullable() }).nullable(),
  tokens: ValuedNumber,
  prompts: Count,
  sessions: Count,
  byTool: ToolSplit,
  mainTool: Tool.nullable(),
  canOpen: z.boolean(),
});
export type MemberUsageRow = z.infer<typeof MemberUsageRow>;

/**
 * One tool connection as a row: my connections on home 'me' (all of them, healthy ones included, problems first),
 * home alerts and the team connection card (only those needing attention).
 */
export const ConnectionAlert = z.object({
  connectionId: z.string(),
  person: PersonRef,
  tool: z.enum(['claude_code', 'codex']),
  deviceName: z.string(),
  status: ConnectionStatus,
  problem: z.boolean(),
  issue: ConnectionIssueKey.nullable(),
  days: z.int().nonnegative().nullable(),
  lastSeenAt: EpochMs.nullable(),
});
export type ConnectionAlert = z.infer<typeof ConnectionAlert>;

/** Person/role summary row used in admin lists. */
export const PersonWithRoles = z.object({ person: PersonRef, team: TeamRef.nullable(), roles: z.array(RoleRef) });

/** Reason a person's row is empty (paused overlapping the period, re-consent pending). */
export const EmptyRowReason = MissingReason;
